import type { Identifier, Reference, ResourceType } from '@medplum/fhirtypes';

import {
  REFERENCE_NOT_EXPANDED,
  REFERENCE_UNAVAILABLE_FALLBACK,
  REFERENCE_UNAVAILABLE_SUFFIX,
} from './copy';

/**
 * A FHIR Reference can point at a target in six distinct ways, each dictating
 * whether it is fetchable and what to show when it can't be expanded.
 */
export type ClassifiedReference =
  | { kind: 'contained'; id: string }
  | {
      kind: 'relative';
      resourceType: ResourceType;
      id: string;
      version?: string;
    }
  | { kind: 'absolute'; url: string }
  | { kind: 'logical'; identifier: Identifier }
  | { kind: 'display-only' }
  | { kind: 'empty' };

/**
 * Why a reference renders as a stub instead of an expanded target. `depth`
 * means the target exists but was not expanded; the others mean it cannot be.
 */
export type StubReason = 'dangling' | 'unfetchable' | 'depth' | 'empty';

const RELATIVE_REF =
  /^([A-Z][A-Za-z]+)\/([A-Za-z0-9\-.]{1,64})(?:\/_history\/([A-Za-z0-9\-.]+))?$/;

export function classifyReference(ref: Reference): ClassifiedReference {
  if (ref.reference) {
    if (ref.reference.startsWith('#')) {
      return { kind: 'contained', id: ref.reference.slice(1) };
    }
    const match = RELATIVE_REF.exec(ref.reference);
    if (match) {
      return {
        kind: 'relative',
        resourceType: match[1] as ResourceType,
        id: match[2] as string,
        version: match[3],
      };
    }
    if (
      ref.reference.startsWith('http://') ||
      ref.reference.startsWith('https://')
    ) {
      return { kind: 'absolute', url: ref.reference };
    }
    return { kind: 'empty' };
  }
  if (ref.identifier) return { kind: 'logical', identifier: ref.identifier };
  if (ref.display) return { kind: 'display-only' };
  return { kind: 'empty' };
}

/**
 * User-facing label for a non-expanded reference. Depth-stopped targets show
 * display-or-type (the resource exists); dangling/unfetchable targets fall
 * through the "(unavailable)" chain. Never an error, never leaks.
 */
export function referenceStubLabel(ref: Reference, reason: StubReason): string {
  const classified = classifyReference(ref);
  const typeLabel =
    classified.kind === 'relative' ? classified.resourceType : undefined;
  if (reason === 'depth') {
    return ref.display ?? typeLabel ?? REFERENCE_NOT_EXPANDED;
  }
  return (
    ref.display ??
    (ref.identifier
      ? `${ref.identifier.system ?? 'identifier'}: ${ref.identifier.value}`
      : undefined) ??
    (typeLabel
      ? `${typeLabel} ${REFERENCE_UNAVAILABLE_SUFFIX}`
      : REFERENCE_UNAVAILABLE_FALLBACK)
  );
}
